import { NavLink, Outlet } from "react-router-dom";
import { FileText, Users, Package, ShieldCheck, LogOut, BarChart3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ChangePasswordButton } from "@/components/ChangePassword";

const tabs = [
  { to: "/sales", label: "Sales", icon: FileText },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/items", label: "Items", icon: Package },
];

const Tab = ({ to, label, icon: Icon }) => (
  <NavLink
    to={to}
    data-testid={`nav-${label.toLowerCase()}`}
    className={({ isActive }) =>
      `flex items-center gap-2 border-b-2 px-1 pb-3 pt-1 text-sm font-semibold transition-colors ${
        isActive ? "border-[#1BA3E0] text-white" : "border-transparent text-slate-300 hover:text-white"
      }`
    }
  >
    <Icon className="h-4 w-4" /> {label}
  </NavLink>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const all = user?.role === "admin" ? [...tabs, { to: "/reports", label: "Reports", icon: BarChart3 }, { to: "/admin", label: "Admin", icon: ShieldCheck }] : tabs;
  return (
    <div className="min-h-screen">
      <header>
        <div className="border-b border-slate-200 bg-white">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
            <div className="flex items-center gap-4">
              <img src="/logo.png" alt="GS Pacific" className="h-9 w-auto" data-testid="header-logo" />
              <span className="hidden border-l border-slate-200 pl-4 text-sm font-semibold text-[#01195D] sm:inline">Sales Portal</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden text-right sm:block">
                <div className="text-sm font-semibold text-[#01195D]" data-testid="header-user-name">{user?.name}</div>
                <div className="text-[11px] uppercase tracking-wider text-[#1BA3E0]" data-testid="header-user-role">{user?.role === "admin" ? "Admin" : "Sales Rep"}</div>
              </div>
              <ChangePasswordButton />
              <Button size="sm" variant="ghost" className="text-slate-500 hover:text-[#01195D]" onClick={logout} data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
        <div className="hidden bg-[#01195D] md:block">
          <nav className="mx-auto flex max-w-7xl gap-7 px-6 pt-3">{all.map((t) => <Tab key={t.to} {...t} />)}</nav>
        </div>
      </header>
      <nav className="fixed inset-x-0 bottom-0 z-40 flex border-t border-white/10 bg-[#01195D] pb-[env(safe-area-inset-bottom)] md:hidden" data-testid="mobile-nav">
        {all.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} data-testid={`mobile-nav-${label.toLowerCase()}`}
            className={({ isActive }) => `flex flex-1 flex-col items-center gap-1 py-2.5 text-[11px] font-semibold ${isActive ? "text-[#1BA3E0]" : "text-slate-300"}`}>
            <Icon className="h-5 w-5" />{label}
          </NavLink>
        ))}
      </nav>
      <main className="mx-auto max-w-7xl px-4 pb-28 pt-6 sm:px-6 md:py-8">
        <Outlet />
      </main>
    </div>
  );
}
